/**
 * Lexical JSON to Content Schema JSON Converter
 *
 * Converts Lexical Editor state JSON to editor-agnostic Content Schema JSON.
 */

import type {
  ActionItemNode as ActionItemNodeCS,
  ChecklistNode as ChecklistNodeCS,
  CodeNode,
  ContentNode,
  DecisionNode as DecisionNodeCS,
  HeadingNode,
  ImageNode,
  LinebreakNode,
  LinkNode,
  ListItemNode,
  ListNode,
  ParagraphNode,
  PriceTrendNode as PriceTrendNodeCS,
  QuoteNode,
  RootNode,
  SelfContractNode as SelfContractNodeCS,
  StockNode as StockNodeCS,
  TagNode as TagNodeCS,
  TextNode,
} from "@hamecho/content-schema";

type JsonObject = Record<string, unknown>;

/**
 * Returns true when payload already matches canonical Content Schema root shape.
 */
export function isContentSchemaFormat(json: unknown): boolean {
  if (!json || typeof json !== "object" || Array.isArray(json)) {
    return false;
  }

  const root = json as JsonObject;
  return root.type === "root" && root.version === 1 && typeof root.key === "string" && Array.isArray(root.children);
}

/**
 * Converts Lexical JSON to canonical Content Schema JSON.
 */
export function convertLexicalToContentSchema(lexicalJSON: string): string {
  let parsed: unknown;

  try {
    parsed = JSON.parse(lexicalJSON);
  } catch (error) {
    throw new Error(`Failed to parse Lexical JSON: ${error}`);
  }

  if (isContentSchemaFormat(parsed)) {
    return JSON.stringify(parsed);
  }

  const lexical = asObject(parsed, "Invalid Lexical JSON: expected object payload");
  const lexicalRoot = asObject(lexical.root, "Invalid Lexical JSON: missing root object");
  if (lexicalRoot.type !== "root") {
    throw new Error("Invalid Lexical JSON: root.type must be 'root'");
  }

  const convertedRoot: RootNode = {
    type: "root",
    version: 1,
    key: asString(lexicalRoot.key, "root"),
    direction: normalizeDirection(lexicalRoot.direction),
    children: convertChildren(lexicalRoot.children),
  };

  return JSON.stringify(convertedRoot);
}

function convertChildren(rawChildren: unknown): ContentNode[] {
  if (!Array.isArray(rawChildren)) {
    return [];
  }

  return rawChildren
    .map((child) => convertNode(child))
    .filter((node): node is ContentNode => {
      if (node === null) return false;

      // Filter out zero-width space (U+200B) placeholder nodes
      // Lexical uses these as placeholders, but they should be rendered as empty text
      if (node.type === "text" && (node as TextNode).text === "\u200B") {
        return false;
      }

      return true;
    });
}

/**
 * Converts a single Lexical node to Content Schema node.
 */
function convertNode(rawNode: unknown): ContentNode | null {
  const node = asObject(rawNode, "Invalid Lexical node: expected object");
  const type = asString(node.type);
  const key = asString(node.key, `${type || "node"}-${Math.random().toString(36).substring(2, 9)}`);

  switch (type) {
    case "paragraph":
      return convertParagraphNode(node, key);
    case "text":
      return convertTextNode(node, key);
    case "link":
      return convertLinkNode(node, key);
    case "linebreak":
      return convertLinebreakNode(key);
    case "image":
      return convertImageNode(node, key);
    case "stock":
      return convertStockNode(node, key);
    case "tag":
      return convertTagNode(node, key);
    case "decision":
      return convertDecisionNode(node, key);
    case "self-contract":
      return convertSelfContractNode(node, key);
    case "checklist":
      return convertChecklistNode(node, key);
    case "price-trend":
      return convertPriceTrendNode(node, key);
    case "action-item":
      return convertActionItemNode(node, key);
    case "heading":
      return convertHeadingNode(node, key);
    case "list":
      return convertListNode(node, key);
    case "listitem":
      return convertListItemNode(node, key);
    case "quote":
      return convertQuoteNode(node, key);
    case "code":
    case "codeblock":
      return convertCodeNode(node, key);
    case "code-highlight":
      return convertTextNode(node, key);
    case "editableLink":
      return convertEditableLinkNode(node, key);
    case "editableImage":
      return convertEditableImageNode(node, key);
    default:
      console.warn(`[content-schema] reject unknown lexical node type: ${type}`);
      throw new Error(`Unknown lexical node type: ${type}`);
  }
}

function convertParagraphNode(lexical: JsonObject, key: string): ParagraphNode {
  return {
    type: "paragraph",
    version: 1,
    key,
    children: convertChildren(lexical.children),
    format: asOptionalString(lexical.format),
    indent: asOptionalNumber(lexical.indent),
    direction: normalizeDirection(lexical.direction),
  };
}

function convertTextNode(lexical: JsonObject, key: string): TextNode {
  return {
    type: "text",
    version: 1,
    key,
    text: asString(lexical.text),
    style: asOptionalString(lexical.style),
    mode: normalizeMode(lexical.mode),
    format: asOptionalNumber(lexical.format),
    detail: asOptionalNumber(lexical.detail),
  };
}

function convertLinkNode(lexical: JsonObject, key: string): LinkNode {
  return {
    type: "link",
    version: 1,
    key,
    children: convertChildren(lexical.children),
    url: asString(lexical.url),
    title: asOptionalString(lexical.title),
    target: normalizeTarget(lexical.target),
    rel: asOptionalString(lexical.rel),
  };
}

function convertLinebreakNode(key: string): LinebreakNode {
  return {
    type: "linebreak",
    version: 1,
    key,
  };
}

function convertImageNode(lexical: JsonObject, key: string): ImageNode {
  const altText = asOptionalString(lexical.altText);
  return {
    type: "image",
    version: 1,
    key,
    src: asString(lexical.src),
    altText: altText || "图片", // 默认 altText，确保后端能提取文本
    width: asOptionalNumber(lexical.width),
    height: asOptionalNumber(lexical.height),
  };
}

function convertEditableLinkNode(lexical: JsonObject, key: string): LinkNode {
  // editableLink doesn't have children in JSON, so we create a TextNode for the link text
  const textContent = asString(lexical.text) || asString(lexical.url);

  return {
    type: "link",
    version: 1,
    key,
    children: [
      {
        type: "text",
        version: 1,
        key: `${key}-text`,
        text: textContent,
      } as TextNode,
    ],
    url: asString(lexical.url),
    target: "_blank",
    rel: "noopener noreferrer",
  };
}

function convertEditableImageNode(lexical: JsonObject, key: string): ImageNode {
  // editableImage has the same properties as regular image for Content Schema
  const altText = asOptionalString(lexical.altText);
  return {
    type: "image",
    version: 1,
    key,
    src: asString(lexical.src),
    altText: altText || "图片", // 默认 altText，确保后端能提取文本
    width: asOptionalNumber(lexical.width),
    height: asOptionalNumber(lexical.height),
  };
}

function convertStockNode(lexical: JsonObject, key: string): StockNodeCS {
  const sentiment = normalizeStockSentiment(lexical.sentiment);
  const confidence = lexical.confidence;

  return {
    type: "stock",
    version: 1,
    key,
    symbol: asString(lexical.symbol),
    sentiment,
    confidence: typeof confidence === "number" ? confidence : confidence === null ? null : undefined,
  };
}

function convertTagNode(lexical: JsonObject, key: string): TagNodeCS {
  const tagId = asString(lexical.tagId);
  if (!tagId) {
    throw new Error("TagNode must have tagId");
  }
  return {
    type: "tag",
    version: 1,
    key,
    tagId,
  };
}

function convertDecisionNode(lexical: JsonObject, key: string): DecisionNodeCS {
  const confidence = lexical.confidence;
  const riskLevel = lexical.riskLevel;
  const timeHorizon = lexical.timeHorizon;

  return {
    type: "decision",
    version: 1,
    key,
    ticker: asString(lexical.ticker),
    contractId: asOptionalString(lexical.contractId),
    decision: normalizeDecision(lexical.decision),
    actionType: normalizeActionType(lexical.actionType),
    price: asNumber(lexical.price, 0),
    quantity: asNumber(lexical.quantity, 0),
    reason: asString(lexical.reason),
    executedAt: asString(lexical.executedAt, new Date().toISOString()),
    confidence: typeof confidence === "number" ? confidence : confidence === null ? null : undefined,
    riskLevel: normalizeRiskLevel(riskLevel),
    timeHorizon: typeof timeHorizon === "string" ? timeHorizon : timeHorizon === null ? null : undefined,
    createdAt: asOptionalString(lexical.createdAt),
  };
}

function convertSelfContractNode(lexical: JsonObject, key: string): SelfContractNodeCS {
  return {
    type: "self-contract",
    version: 1,
    key,
    ticker: asString(lexical.ticker),
    template: normalizeContractTemplate(lexical.template),
    expiration: asString(lexical.expiration),
    idea: asString(lexical.idea),
    trigger: normalizeContractTrigger(lexical),
    action: asString(lexical.action),
    status: normalizeContractStatus(lexical.status),
    createdAt: asOptionalString(lexical.createdAt),
    updatedAt: asOptionalString(lexical.updatedAt),
  };
}

function convertChecklistNode(lexical: JsonObject, key: string): ChecklistNodeCS | null {
  const rawItems = Array.isArray(lexical.items) ? lexical.items : [];
  if (rawItems.length === 0) {
    return null;
  }

  const items = rawItems.map((rawItem, index) => {
    const item = asObject(rawItem, "Invalid checklist item: expected object");
    return {
      id: asString(item.id, `item-${index + 1}`),
      text: asString(item.text),
      checked: item.checked === true,
      category: asOptionalString(item.category),
    };
  });

  return {
    type: "checklist",
    version: 1,
    key,
    items,
    title: asOptionalString(lexical.title),
    templateId: asOptionalString(lexical.templateId),
  };
}

function convertPriceTrendNode(lexical: JsonObject, key: string): PriceTrendNodeCS {
  return {
    type: "price-trend",
    version: 1,
    key,
    symbol: asString(lexical.symbol),
    trend: normalizeTrend(lexical.trend),
    support: asNumber(lexical.support, 0),
    resistance: asNumber(lexical.resistance, 0),
    target: asNumber(lexical.target, 0),
    stopLoss: asOptionalNumber(lexical.stopLoss),
    notes: asOptionalString(lexical.notes),
    chartUrl: asOptionalString(lexical.chartUrl),
  };
}

function convertActionItemNode(lexical: JsonObject, key: string): ActionItemNodeCS {
  return {
    type: "action-item",
    version: 1,
    key,
    action: asString(lexical.action),
    priority: normalizePriority(lexical.priority),
    status: normalizeActionItemStatus(lexical.status),
    dueDate: asOptionalString(lexical.dueDate),
    assignee: asOptionalString(lexical.assignee),
    notes: asOptionalString(lexical.notes),
  };
}

function convertHeadingNode(lexical: JsonObject, key: string): HeadingNode {
  const tag = normalizeHeadingTag(lexical.tag);
  return {
    type: "heading",
    version: 1,
    key,
    tag,
    children: convertChildren(lexical.children),
  };
}

function convertListNode(lexical: JsonObject, key: string): ListNode {
  const listType = normalizeListType(lexical.listType);
  return {
    type: "list",
    version: 1,
    key,
    listType,
    start: asOptionalNumber(lexical.start),
    children: convertChildren(lexical.children),
  };
}

function convertListItemNode(lexical: JsonObject, key: string): ListItemNode {
  return {
    type: "listitem",
    version: 1,
    key,
    checked: lexical.checked === true || lexical.checked === false ? (lexical.checked as boolean) : undefined,
    children: convertChildren(lexical.children),
  };
}

function convertQuoteNode(lexical: JsonObject, key: string): QuoteNode {
  return {
    type: "quote",
    version: 1,
    key,
    children: convertChildren(lexical.children),
  };
}

function convertCodeNode(lexical: JsonObject, key: string): CodeNode {
  return {
    type: "code",
    version: 1,
    key,
    language: asOptionalString(lexical.language),
    children: convertChildren(lexical.children),
  };
}

function normalizeDirection(value: unknown): "ltr" | "rtl" | "null" | undefined {
  if (value === "ltr" || value === "rtl" || value === "null") {
    return value;
  }
  if (value === null) {
    return "null";
  }
  return undefined;
}

function normalizeMode(value: unknown): "normal" | "token" | "segmented" | undefined {
  if (value === "normal" || value === "token" || value === "segmented") {
    return value;
  }
  return undefined;
}

function normalizeTarget(value: unknown): "_blank" | "_self" | "_parent" | "_top" | undefined {
  if (value === "_blank" || value === "_self" || value === "_parent" || value === "_top") {
    return value;
  }
  return undefined;
}

function normalizeStockSentiment(value: unknown): "bullish" | "bearish" | "neutral" {
  if (value === "bullish" || value === "bearish" || value === "neutral") {
    return value;
  }
  return "neutral";
}

function normalizeDecision(value: unknown): "BUY" | "SELL" | "HOLD" {
  if (value === "BUY" || value === "SELL" || value === "HOLD") {
    return value;
  }
  return "HOLD";
}

function normalizeActionType(value: unknown): "FOLLOW" | "OVERRIDE" | "NONE" {
  if (value === "FOLLOW" || value === "OVERRIDE" || value === "NONE") {
    return value;
  }
  return "NONE";
}

function normalizeRiskLevel(value: unknown): "LOW" | "MEDIUM" | "HIGH" | null | undefined {
  if (value === "LOW" || value === "MEDIUM" || value === "HIGH") {
    return value;
  }
  if (value === null) {
    return null;
  }
  return undefined;
}

function normalizeContractTemplate(value: unknown): "value-oriented" | "trading-oriented" {
  if (value === "value-oriented" || value === "trading-oriented") {
    return value;
  }
  return "value-oriented";
}

function normalizeContractStatus(value: unknown): "active" | "triggered" | "cancelled" | "expired" {
  if (value === "active" || value === "triggered" || value === "cancelled" || value === "expired") {
    return value;
  }
  return "active";
}

function normalizeContractTrigger(value: JsonObject): SelfContractNodeCS["trigger"] {
  const rawType = value.triggerType;
  const rawPriceLevel = value.triggerPriceLevel;
  const rawOperator = value.triggerOperator;
  const rawNotes = value.triggerNotes;

  return {
    type: rawType === "price" || rawType === "expiration" || rawType === "time" || rawType === "custom" ? rawType : "price",
    priceLevel: asOptionalNumber(rawPriceLevel),
    operator:
      rawOperator === ">=" || rawOperator === "<=" || rawOperator === "==" || rawOperator === ">" || rawOperator === "<"
        ? rawOperator
        : undefined,
    notes: asOptionalString(rawNotes),
  };
}

function normalizeTrend(value: unknown): "BULLISH" | "BEARISH" | "NEUTRAL" {
  if (value === "BULLISH" || value === "BEARISH" || value === "NEUTRAL") {
    return value;
  }
  return "NEUTRAL";
}

function normalizePriority(value: unknown): "high" | "medium" | "low" | undefined {
  if (value === "high" || value === "medium" || value === "low") {
    return value;
  }
  return undefined;
}

function normalizeActionItemStatus(value: unknown): "todo" | "in-progress" | "done" | undefined {
  if (value === "todo" || value === "in-progress" || value === "done") {
    return value;
  }
  return undefined;
}

function normalizeHeadingTag(value: unknown): HeadingNode["tag"] {
  if (value === "h1" || value === "h2" || value === "h3" || value === "h4" || value === "h5" || value === "h6") {
    return value;
  }
  return "h1";
}

function normalizeListType(value: unknown): ListNode["listType"] {
  if (value === "bullet" || value === "number" || value === "check") {
    return value;
  }
  return "bullet";
}

function asObject(value: unknown, errorMessage: string): JsonObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(errorMessage);
  }
  return value as JsonObject;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asOptionalNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
